import type { Tracker } from './consent'

/**
 * Taking consent back - FRONTEND.md §9.738, owner O108.
 *
 * **Why a withdrawal needs more than a new decision.** `useConsentedTrackers` injects Google Analytics, the Meta
 * pixel and TikTok once each and never removes them, because a script already running cannot be unloaded. So a
 * customer who turns a category off would still be tracked for the rest of the visit, by a tracker that also keeps
 * the identifier cookie it wrote. GDPR art. 7(3) asks that withdrawing be as easy as giving, and *easy* is no use if
 * it does not take effect.
 *
 * So when a decision takes back a category, every refused tracker the page runs is stopped with its own switch, the
 * category's cookies are deleted, and - when the page runs anything refused - the page reloads: after the reload
 * nothing the customer refused is injected, because the gate reads the new decision, and the load after it deletes
 * the cookies once more. Granting more needs none of it - the trackers' hook injects what is newly permitted on the
 * decision event, as it always has.
 *
 * **Measured against what the page runs, not only against what is stored** - corrected after review. The stored
 * decision is the same for every tab of the site, so a tab that had loaded Google Analytics before another tab
 * withdrew would compare *off* with *off* and keep tracking. The trackers' hook compares each decision with the one it
 * last obeyed and with the trackers it injected (`refusedTrackers`), and hears another tab's decision through
 * `storage`.
 *
 * The rules are pure and tested on their own; `forgetTrackerCookies`, `rememberWithdrawal` and `finishWithdrawal` are
 * the thin side effects around them.
 */

export type ConsentCategory = 'analytics' | 'marketing'

/** The two optional categories, in the order the card draws them. */
export const CONSENT_CATEGORIES: readonly ConsentCategory[] = ['analytics', 'marketing']

type Choice = Readonly<Record<ConsentCategory, boolean>>

/**
 * The categories a new decision turns off that the earlier one had on.
 *
 * `before` is the decision obeyed until now, or `null` when there was none - a first answer withdraws nothing,
 * because nothing was permitted before it. `after` is `null` when the record is gone, which permits nothing.
 */
export function withdrawnCategories(before: Choice | null, after: Choice | null): ConsentCategory[] {
  if (before === null) return []
  return CONSENT_CATEGORIES.filter((category) => before[category] && !(after !== null && after[category]))
}

/**
 * The category each tracker sits behind - `permittedTrackers`' own split, GA behind `analytics` and both pixels behind
 * `marketing`. `withdrawal.test.ts` holds the two to each other, so a tracker moved in one cannot be taken back under
 * the other.
 */
const CATEGORY_OF: Readonly<Record<Tracker, ConsentCategory>> = {
  'google-analytics': 'analytics',
  'meta-pixel': 'marketing',
  'tiktok-pixel': 'marketing',
}

export function trackerCategory(tracker: Tracker): ConsentCategory {
  return CATEGORY_OF[tracker]
}

/**
 * The trackers this page runs that a decision no longer permits.
 *
 * What a withdrawal is measured against, because a script cannot be taken back: it runs until the page goes, whatever
 * the stored record says. Asked of the category alone, not of the configured ids, so a tracker whose id disappears
 * from the settings is not mistaken for consent taken back. `null` - no decision on record - permits nothing.
 */
export function refusedTrackers(loaded: Iterable<Tracker>, decision: Choice | null): Tracker[] {
  return [...loaded].filter((tracker) => decision === null || !decision[CATEGORY_OF[tracker]])
}

/**
 * The categories whose cookies a decision deletes: those it takes back from the decision obeyed before it, and those
 * of every refused tracker the page runs - which is how a tab whose record already says *off* still takes back a
 * tracker it loaded before another tab changed the record.
 */
export function categoriesToForget(
  before: Choice | null,
  after: Choice | null,
  refused: readonly Tracker[],
): ConsentCategory[] {
  const named = new Set<ConsentCategory>([
    ...withdrawnCategories(before, after),
    ...refused.map((tracker) => CATEGORY_OF[tracker]),
  ])
  return CONSENT_CATEGORIES.filter((category) => named.has(category))
}

/**
 * The window property Google's own opt-out reads: `window['ga-disable-<measurement id>'] = true`.
 *
 * gtag checks it on every event, so once it is set the `user_engagement` event GA raises on `pagehide` stops before it
 * re-stamps `_ga_<id>` or sends the client id - which it otherwise does during the very reload that follows a
 * withdrawal, undoing the deletion (corrected after review).
 */
export function gaOptOutKey(measurementId: string): string {
  return `ga-disable-${measurementId}`
}

/**
 * The cookies each category's trackers write on this site, by name.
 *
 * `analytics` is Google Analytics: `_ga`, one `_ga_<property>` per measurement id, and the older `_gid` and
 * `_gat` / `_gat_<tracker>`. `marketing` is the Meta pixel's `_fbp` and `_fbc` and TikTok's `_ttp`,
 * `_tt_enable_cookie`, `ttcsid` and `ttcsid_<pixel>`. The split is `permittedTrackers`' own: GA behind
 * `analytics`, both pixels behind `marketing`. A category still granted keeps its cookies, so withdrawing
 * marketing does not reset the analytics identifier somebody agreed to.
 */
const TRACKER_COOKIES: Record<ConsentCategory, readonly RegExp[]> = {
  analytics: [/^_ga$/, /^_ga_/, /^_gid$/, /^_gat$/, /^_gat_/],
  marketing: [/^_fbp$/, /^_fbc$/, /^_ttp$/, /^_tt_enable_cookie$/, /^ttcsid$/, /^ttcsid_/],
}

/** Whether a cookie name is one the trackers of these categories write. */
export function isTrackerCookie(name: string, categories: readonly ConsentCategory[]): boolean {
  return categories.some((category) => TRACKER_COOKIES[category].some((pattern) => pattern.test(name)))
}

/**
 * The names in a `document.cookie` string that belong to these categories' trackers, each once.
 *
 * Read off the jar rather than listed, because `_ga_<property>` and `ttcsid_<pixel>` carry an id nobody here
 * knows. A name appears twice when a host cookie and a parent-domain cookie share it, so the list is de-duplicated
 * and every name is expired on every domain below.
 */
export function trackerCookieNames(cookieHeader: string, categories: readonly ConsentCategory[]): string[] {
  const names = cookieHeader
    .split(';')
    .map((pair) => (pair.split('=')[0] ?? '').trim())
    .filter((name) => name !== '' && isTrackerCookie(name, categories))
  return [...new Set(names)]
}

/**
 * This host and every parent domain, longest first: `nesvady.pizzamajstro.sk`, `pizzamajstro.sk`, `sk`.
 *
 * Google Analytics writes `_ga` on the widest domain the browser accepts, so it usually sits on the parent rather
 * than on the host the customer is reading, and a cookie can only be removed on the domain it was set for. The
 * top-level label is tried as well rather than special-cased: a browser refuses a cookie on a public suffix, so
 * that one write is a no-op, and knowing which suffixes are public would need a list this file should not carry.
 *
 * An address (`10.0.0.5`, `[::1]`) has no parent domains, so it answers itself alone.
 */
export function cookieDomains(hostname: string): string[] {
  const host = hostname.trim().toLowerCase().replace(/\.$/, '')
  if (host === '') return []
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.includes(':')) return [host]
  const labels = host.split('.')
  return labels.map((_, index) => labels.slice(index).join('.'))
}

const LONG_AGO = 'Thu, 01 Jan 1970 00:00:00 GMT'

/**
 * Every `document.cookie` assignment that removes these categories' cookies, built where it can be tested.
 *
 * Each name is expired once without a domain, which is how a cookie set for this host alone is removed, and once
 * per domain from `cookieDomains`. `path=/` is where all of these trackers write.
 */
export function expiringCookies(
  cookieHeader: string,
  categories: readonly ConsentCategory[],
  hostname: string,
): string[] {
  const domains = cookieDomains(hostname)
  return trackerCookieNames(cookieHeader, categories).flatMap((name) => {
    const expired = `${name}=; expires=${LONG_AGO}; max-age=0; path=/`
    return [expired, ...domains.map((domain) => `${expired}; domain=${domain}`)]
  })
}

/** Deletes these categories' tracker cookies from this page's jar. */
export function forgetTrackerCookies(categories: readonly ConsentCategory[]): void {
  if (typeof document === 'undefined' || typeof location === 'undefined') return
  for (const assignment of expiringCookies(document.cookie, categories, location.hostname)) {
    document.cookie = assignment
  }
}

/**
 * Where a withdrawal leaves a note for the page load after it.
 *
 * `sessionStorage`, because the page that reads it is this tab's next one: the reload a withdrawal ends with keeps the
 * tab's session storage. Every other tab hears the withdrawal for itself and leaves its own note if it reloads.
 */
export const WITHDRAWAL_KEY = 'fk-cookie-withdrawal-v1'

/**
 * The categories a note left by a withdrawal still asks to delete.
 *
 * Only what the stored decision still refuses: a category granted again between the note and the next page load has
 * a new cookie the customer agreed to, and deleting it would take back a grant. A note that does not parse names
 * nothing, so a stray value in the key cannot delete anything.
 */
export function pendingWithdrawal(note: string | null, stored: Choice | null): ConsentCategory[] {
  if (note === null) return []
  let named: unknown
  try {
    named = JSON.parse(note)
  } catch {
    return []
  }
  if (!Array.isArray(named)) return []
  const listed = new Set<unknown>(named)
  return CONSENT_CATEGORIES.filter((category) => listed.has(category) && !(stored !== null && stored[category]))
}

/**
 * Leaves the note: these categories were taken back and the page is about to reload.
 *
 * Added to a note already there rather than replacing it, so two decisions heard before the page goes - this tab's own
 * and another tab's - both reach the next load.
 */
export function rememberWithdrawal(categories: readonly ConsentCategory[]): void {
  try {
    if (typeof sessionStorage === 'undefined') return
    const earlier = pendingWithdrawal(sessionStorage.getItem(WITHDRAWAL_KEY), null)
    const all = CONSENT_CATEGORIES.filter((category) => categories.includes(category) || earlier.includes(category))
    sessionStorage.setItem(WITHDRAWAL_KEY, JSON.stringify(all))
  } catch {
    // A locked-down profile throws. The deletion already done stands; only its repeat on the next load is lost.
  }
}

/**
 * Deletes, once more, what a withdrawal on the page before this one took back, then removes the note.
 *
 * A tracker still running while that page unloaded can write its cookie after the deletion - GA4's `pagehide` event
 * does exactly that unless it was stopped (corrected after review). This page loads nothing refused, so a deletion
 * here is the last word. `stored` is the decision on record now; see `pendingWithdrawal` for why it matters.
 */
export function finishWithdrawal(stored: Choice | null): void {
  let note: string | null
  try {
    if (typeof sessionStorage === 'undefined') return
    note = sessionStorage.getItem(WITHDRAWAL_KEY)
    if (note === null) return
    sessionStorage.removeItem(WITHDRAWAL_KEY)
  } catch {
    return
  }
  forgetTrackerCookies(pendingWithdrawal(note, stored))
}
